import { ReportInProgressType } from 'QuoteApp/screens/Reports/types';
import {
  GetDetailsResponseType,
  ReportType,
  ThirdPartyReports,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import { getErrorMessage } from 'QuoteApp/utils';
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useAppContext } from '../AppProvider';

export type reportCommandNames =
  | 'driverDiscovery'
  | 'driverDataEnhancement'
  | 'ncf'
  | 'ncfCc'
  | 'clue'
  | 'clueMvr';

export type ReportsProviderType = {
  reportInProgress: ReportInProgressType;
  changeReportInProgress(report: ReportInProgressType): void;
  reportsDisabled: boolean;
  acknowledgeDisclosure: (
    disclosure: Disclosures,
    throwError?: boolean
  ) => Promise<void>;
  completingDisclosure: boolean;
  reports: {
    driverDiscovery?: ReportType;
    driverDataEnhancement?: ReportType;
    ncf?: ReportType;
    ncfCc?: ReportType;
    clue?: ReportType;
    clueMvr?: ReportType;
  };
};

export const reportCommandMappings: { [key: string]: reportCommandNames } = {
  [ThirdPartyReports.DriverDiscovery]: 'driverDiscovery',
  [ThirdPartyReports.DriverDataEnhancement]: 'driverDataEnhancement',
  [ThirdPartyReports.Ncf]: 'ncf',
  [ThirdPartyReports.NcfAndCurrentCarrier]: 'ncfCc',
  [ThirdPartyReports.Clue]: 'clue',
  [ThirdPartyReports.All]: 'clueMvr',
};

export const ReportsProviderContext = createContext<
  ReportsProviderType | undefined
>(undefined);

export const useReportsContext = () => {
  // https://stackoverflow.com/a/69735347
  const reportsProviderContext = useContext(ReportsProviderContext);
  if (!reportsProviderContext)
    throw new Error(
      'No ReportsProviderContext.Provider found when calling useReportsContext.'
    );
  return reportsProviderContext;
};

export const ReportsProvider: FC<{
  children: (ctx: ReportsProviderType) => ReactNode;
}> = ({ children }) => {
  const { appId, details, setDetails } = useAppContext();
  const { toast } = useToast();
  const [reportInProgress, changeReportInProgress] = useState<
    ReportInProgressType
  >(null);

  const initialDisclosureDocument = details.disclosures.find(
    doc => doc.attributes.documentName === Disclosures.Initial
  );

  const initialDisclosureCompleted =
    initialDisclosureDocument && initialDisclosureDocument.attributes.completed;

  // disable reports when we have not completed the initial disclosure
  const reportsDisabled = Boolean(!initialDisclosureCompleted);

  const [
    { loading: completingDisclosure },
    postReportDisclosure,
  ] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    transformResponse: transformAppDetails,
  });

  const acknowledgeDisclosure = useCallback(
    async (disclosure: Disclosures, throwError: boolean = true) => {
      const disclosureReportData = details.disclosures.find(
        doc => doc.attributes.documentName === `${disclosure}`
      );

      // if the disclosure has not been completed post to the endpoint
      if (disclosureReportData && !disclosureReportData.attributes.completed) {
        try {
          const reportDisclosureCompleted = await postReportDisclosure({
            url: `insurance_applications/${appId}/document_signatures/${disclosure}/complete`,
          });
          setDetails(reportDisclosureCompleted.data);
        } catch (e) {
          toast({
            type: 'error',
            message: `Error capturing disclosure: ${getErrorMessage(
              e
            )}. Please try again.`,
            withSecondaryNav: false,
            icon: 'error',
          });
          if (throwError) {
            // throw again to prevent reports
            throw new Error('Failed to capture disclosure');
          }
        }
      }
    },
    [appId, details.disclosures, postReportDisclosure, setDetails, toast]
  );

  const reports = useMemo(
    () =>
      details.commands.reports.reduce(
        (memo: { [key: string]: ReportType }, report) => {
          const key: string = reportCommandMappings[report.reportName];
          if (key) {
            memo[key] = report;
          }
          return memo;
        },
        {}
      ),
    [details]
  );

  const providerValues = useMemo(
    () => ({
      acknowledgeDisclosure,
      changeReportInProgress,
      completingDisclosure,
      reportInProgress,
      reports,
      reportsDisabled,
    }),
    [
      acknowledgeDisclosure,
      completingDisclosure,
      reportInProgress,
      reports,
      reportsDisabled,
    ]
  );

  return (
    <ReportsProviderContext.Provider value={providerValues}>
      {children(providerValues)}
    </ReportsProviderContext.Provider>
  );
};
